/**
 * Curated dialogue lookup for v0.1 NPCs and signs across Pallet/Route 1/Viridian.
 *
 * Keys are pokefirered script references (e.g. `PalletTown_EventScript_FatMan`).
 * Values are arrays of message pages — each page renders in the dialogue box;
 * pressing A advances to the next; on the last page, pressing A closes.
 *
 * These are NOT the cartridge texts (those live in pokefirered's data/scripts/
 * .inc files and would be a multi-day translation effort). Lines are pragmatic
 * stand-ins that match the spirit of each NPC. Replace with curated copy when
 * the friend group plays and individual NPCs become memorable.
 *
 * Unknown script refs fall through to a single ellipsis page.
 */

export const DIALOGUE: Record<string, string[]> = {
  // ── Pallet Town ────────────────────────────────────────────
  PalletTown_EventScript_SignLady: [
    'I love this town.',
    'It is the dawn of a new adventure!',
  ],
  PalletTown_EventScript_FatMan: [
    'Technology is incredible.',
    'You can store and recall POKEMON via the PC.',
  ],
  PalletTown_EventScript_OaksLabSign: ['OAK POKEMON RESEARCH LAB'],
  PalletTown_EventScript_PlayersHouseSign: ['RED\'s house'],
  PalletTown_EventScript_RivalsHouseSign: ['BLUE\'s house'],
  PalletTown_EventScript_TownSign: [
    'PALLET TOWN',
    'Shades of your journey await!',
  ],
  PalletTown_EventScript_TrainerTips: [
    "TIP: Tall grass hides wild POKEMON.",
    'Step carefully.',
  ],

  // ── Route 1 ────────────────────────────────────────────────
  Route1_EventScript_MartClerk: [
    'Hi! I work at the POKEMON MART.',
    'Drop by sometime.',
  ],
  Route1_EventScript_Boy: [
    'See those ledges along the road?',
    "Once you hop down, you can't climb back up.",
  ],
  Route1_EventScript_RouteSign: [
    'ROUTE 1',
    'PALLET TOWN — VIRIDIAN CITY',
  ],

  // ── Viridian City ──────────────────────────────────────────
  ViridianCity_EventScript_DreamEaterTutor: [
    'I came here on vacation.',
    'The air smells like leaves.',
  ],
  ViridianCity_EventScript_OldMan: [
    'I had my coffee this morning.',
    "I'm in a fine mood.",
  ],
  // Cartridge: the catching-tutorial man. This game has no catching —
  // the draft IS acquisition, and he knows it.
  ViridianCity_EventScript_TutorialOldMan: [
    'Ahh, I have had my coffee and I feel great!',
    'You want to see how to catch POKEMON?',
    'Hah! Nobody catches them here.',
    'The routes OFFER — you choose.',
  ],
  ViridianCity_EventScript_Woman: [
    'Welcome to VIRIDIAN CITY.',
    'The eternally green paradise.',
  ],
  ViridianCity_EventScript_Youngster: [
    "I'm a little restless today.",
    'Maybe I will explore the forest.',
  ],
  ViridianCity_EventScript_Boy: [
    "I dropped something around here.",
    "Have you seen it?",
  ],
  ViridianCity_EventScript_TrainerTips1: [
    "TIP: Press B to cancel.",
  ],
  ViridianCity_EventScript_TrainerTips2: [
    'TIP: Wild POKEMON live in tall grass.',
  ],
  ViridianCity_EventScript_GymSign: [
    'VIRIDIAN CITY POKEMON GYM',
    'LEADER: ?',
    'The trainer who endured.',
  ],
  ViridianCity_EventScript_CitySign: [
    'VIRIDIAN CITY',
    'The Eternally Green Paradise',
  ],
  ViridianCity_EventScript_GymDoor: ['The door is locked.'],
  ViridianCity_EventScript_GymDoorLocked: ['The door is locked.'],
  ViridianCity_EventScript_RoadBlocked: [
    'Hold on there!',
    'You\'re not allowed through yet.',
  ],
  EventScript_CutTree: [
    "It's a small tree.",
    'Looks like it could be cut down.',
  ],
  ViridianCity_EventScript_ItemPotion: ['You found a POTION!'],

  // ── Player\'s House 1F ──────────────────────────────────────
  PalletTown_PlayersHouse_1F_EventScript_Mom: [
    'Right. All boys leave home some day.',
    'It said so on TV.',
    'PROF. OAK is looking for you.',
  ],
  PalletTown_PlayersHouse_1F_EventScript_TV: [
    'There is a movie on TV.',
    'Two men are dancing on a big piano keyboard.',
    'Better get going!',
  ],

  // ── Oak\'s Lab ──────────────────────────────────────────────
  // PR A1: _EventScript_ProfOak + _EventScript_Rival are now routed
  // through scriptRunner handlers (labOakTap + labRivalTap) for
  // state-aware tap dialogue. The static entries below are intentionally
  // gone — the new handlers dispatch per labScene state.
  PalletTown_ProfessorOaksLab_EventScript_BulbasaurBall: [
    'BULBASAUR — the SEED POKEMON.',
    'Easy to raise.',
  ],
  PalletTown_ProfessorOaksLab_EventScript_CharmanderBall: [
    'CHARMANDER — the LIZARD POKEMON.',
    'Has a fiery tail.',
  ],
  PalletTown_ProfessorOaksLab_EventScript_SquirtleBall: [
    'SQUIRTLE — the TINY TURTLE POKEMON.',
    'Sturdy and reliable.',
  ],
  PalletTown_ProfessorOaksLab_EventScript_Aide1: ['Welcome to the lab!'],
  PalletTown_ProfessorOaksLab_EventScript_Aide2: ['Working hard, as always.'],
  PalletTown_ProfessorOaksLab_EventScript_Aide3: [
    'I help PROF. OAK with research.',
  ],
  PalletTown_ProfessorOaksLab_EventScript_Pokedex: [
    'POKEDEX — a high-tech encyclopedia.',
  ],
  PalletTown_ProfessorOaksLab_EventScript_Computer: [
    'There is an e-mail on the screen.',
    'POKEMON LEAGUE news. Better not snoop.',
  ],
  // Cartridge signs teach START-menu + SAVE. This cartridge has a
  // Pokéball glyph and autosave — the posters teach what's true here.
  PalletTown_ProfessorOaksLab_EventScript_LeftSign: [
    'Tap the POKE BALL up top to open the menu.',
  ],
  PalletTown_ProfessorOaksLab_EventScript_RightSign: [
    'This game saves by itself.',
    'Adventure without fear.',
  ],
  // PR A1: _EventScript_Rival is routed through scriptRunner's
  // labRivalTap handler — see handlers/index.ts. Static entry removed.

  // ── Route 2 ────────────────────────────────────────────────
  // The cartridge ball here holds an ETHER; PP isn't persisted between
  // battles in this game, so it grants a SUPER POTION (see ITEM_BALLS).
  Route2_EventScript_ItemEther: ['You found a SUPER POTION!'],
  Route2_EventScript_ItemParalyzeHeal: ['You found a PARALYZ HEAL!'],
  Route2_EventScript_DiglettsCaveSign: [
    "DIGLETT'S CAVE",
    '(Cave maps not converted in v0.1.)',
  ],
  Route2_EventScript_RouteSign: ['ROUTE 2', 'VIRIDIAN CITY — PEWTER CITY'],

  // ── Viridian Forest gates (south + north) ──────────────────
  Route2_ViridianForest_SouthEntrance_EventScript_Woman1: [
    'TRAINERS hide in the tall grass and wait.',
    'Spooky.',
  ],
  Route2_ViridianForest_SouthEntrance_EventScript_Woman2: [
    'Watch out for BUG-type POKEMON.',
    'Stings have a way of ruining a good day.',
  ],
  Route2_ViridianForest_NorthEntrance_EventScript_Youngster: [
    'I made it through the forest!',
    'Now to find PEWTER CITY.',
  ],
  Route2_ViridianForest_NorthEntrance_EventScript_OldMan: [
    "I'm taking a break before heading back.",
    'Mind those CATERPIE.',
  ],
  Route2_ViridianForest_NorthEntrance_EventScript_CooltrainerF: [
    "TIP: A POKEMON'S level matters more than its TYPE.",
  ],

  // ── Viridian Forest ────────────────────────────────────────
  ViridianForest_EventScript_Youngster: [
    "I'm hunting BUG-type POKEMON.",
    'They make great early team members.',
  ],
  ViridianForest_EventScript_Boy: [
    'I lost my way in here once.',
    'Stay near the path.',
  ],
  // Cartridge ball holds a POKE BALL; no catching here — it grants a
  // POTION (see ITEM_BALLS).
  ViridianForest_EventScript_ItemPokeBall: ['You found a POTION!'],
  ViridianForest_EventScript_ItemAntidote: ['You found an ANTIDOTE!'],
  ViridianForest_EventScript_ItemPotion: ['You found a POTION!'],
  ViridianForest_EventScript_ItemPotion2: ['You found a POTION!'],
  ViridianForest_EventScript_TrainerTips1: [
    'TIP: TRAINERS spot you from across a row of grass.',
    'Plan your steps.',
  ],
  ViridianForest_EventScript_TrainerTips2: [
    'TIP: Switch POKEMON to manage matchups.',
  ],
  ViridianForest_EventScript_TrainerTips3: ['TIP: Status effects persist after battle.'],
  ViridianForest_EventScript_TrainerTips4: [
    'TIP: Heal at the next POKEMON CENTER.',
    'Run-ending KOs are forever.',
  ],
  ViridianForest_EventScript_TrainerTips5: [
    'TIP: Pay attention to type matchups.',
    'A super-effective hit decides battles.',
  ],
  ViridianForest_EventScript_ExitSign: [
    'VIRIDIAN FOREST',
    'PEWTER CITY AHEAD',
  ],

  // ── Pewter City ────────────────────────────────────────────
  PewterCity_EventScript_Lass: [
    'I came to PEWTER to see the MUSEUM.',
    'Fossils everywhere.',
  ],
  PewterCity_EventScript_MuseumGuide: [
    'The MUSEUM is over there.',
    '(Closed for v0.1.)',
  ],
  PewterCity_EventScript_FatMan: [
    'PEWTER CITY is grey.',
    'Stone everywhere you look.',
  ],
  PewterCity_EventScript_BugCatcher: [
    'I caught all my POKEMON in VIRIDIAN FOREST.',
    "I'm a forest expert.",
  ],
  PewterCity_EventScript_GymGuide: [
    'BROCK is the GYM LEADER here.',
    'His ROCK-type POKEMON hit hard.',
    'Bring a GRASS or WATER type.',
  ],
  PewterCity_EventScript_RunningShoesAide: [
    'A gift from PROF. OAK!',
    "(Running Shoes aren't implemented in v0.1.)",
  ],
  PewterCity_EventScript_MuseumSign: [
    'PEWTER MUSEUM OF SCIENCE',
    '(Not converted in v0.1.)',
  ],
  PewterCity_EventScript_PoliceNotice: [
    'NOTICE',
    'Watch for trainers in tall grass.',
  ],
  PewterCity_EventScript_GymSign: [
    'PEWTER CITY POKEMON GYM',
    'LEADER: BROCK',
    'The Rock-Solid POKEMON Trainer!',
  ],
  PewterCity_EventScript_TrainerTips: [
    'TIP: A POKEMON CENTER restores your party.',
    'No charge.',
  ],
  PewterCity_EventScript_CitySign: [
    'PEWTER CITY',
    'A Stone Gray City',
  ],

  // ── Pewter Gym ────────────────────────────────────────────
  PewterCity_Gym_EventScript_GymGuy: [
    "Yo, CHAMP-IN-MAKING!",
    "BROCK uses ROCK types — TACKLE just bounces off them.",
    "GRASS or WATER moves will end this fast.",
  ],
  PewterCity_Gym_EventScript_GymStatue: [
    'PEWTER CITY POKEMON GYM',
    'BROCK\'S WINNING TRAINERS:',
    '(your name here?)',
  ],

  // Trainer NPCs (Brock + Liam in gym, bug-catchers in forest) get
  // intro/defeat text from data/trainerParties.ts in M4. Until then,
  // tapping them shows this placeholder so they're not silent.
  // Trainer entries are the POST-DEFEAT tap path only — undefeated
  // trainers battle on interact (talk-to-battle branch in tryInteract).
  PewterCity_Gym_EventScript_Brock: [
    'There are all kinds of trainers in this world.',
    'You and your POKEMON are among the strong ones.',
  ],
  PewterCity_Gym_EventScript_Liam: [
    "You beat me fair and square.",
    "BROCK is another story entirely.",
  ],
  ViridianForest_EventScript_Rick: ['My BUGS never stood a chance...'],
  ViridianForest_EventScript_Doug: ['Beaten! My WEEDLE did its best.'],
  ViridianForest_EventScript_Sammy: ['I need to catch tougher BUGS.'],
  ViridianForest_EventScript_Anthony: ['You fight like a GYM LEADER.'],
  ViridianForest_EventScript_Charlie: ['The forest is yours. Go on ahead.'],

  // ── Pewter Pokémon Center 1F ──────────────────────────────
  // Nurse is wired to the M3 heal action via HEAL_SPOT_SCRIPT_REFS in
  // main.ts: closing this dialogue triggers fade-out → healAll() → save
  // → fade-in. v0.1 has no Y/N prompt; the heal is unconditional.
  PewterCity_PokemonCenter_1F_EventScript_Nurse: [
    'Welcome to our POKEMON CENTER.',
    "We'll restore your POKEMON to full health.",
    "Thank you for waiting!",
    "Your POKEMON are fully healed.",
  ],
  PewterCity_PokemonCenter_1F_EventScript_Jigglypuff: [
    'JIGGLYPUFF: Jigglyyy~',
  ],
  PewterCity_PokemonCenter_1F_EventScript_Gentleman: [
    'I came to heal up before pressing on.',
  ],
  PewterCity_PokemonCenter_1F_EventScript_Youngster: [
    "I lost my last POKEMON to BROCK.",
    "I'm starting over.",
  ],
  MysteryEventClub_EventScript_Woman: [
    'MYSTERY EVENTS!',
    '(Not implemented in v0.1.)',
  ],
  PewterCity_PokemonCenter_1F_EventScript_GBAKid1: [
    'My friend has a great POKEMON team.',
  ],
  PewterCity_PokemonCenter_1F_EventScript_GBAKid2: [
    'I trade POKEMON with my friends.',
    '(Trading not in v0.1.)',
  ],

  // ── Viridian Pokémon Center 1F (MVP PR 2b) ────────────────
  // Nurse routes through HEAL_SPOT_SCRIPT_REFS — same fade/heal/save/ack
  // path as Pewter. Three idle NPCs flesh out the room; their lines are
  // stand-ins until friend playtest tells us which ones are memorable.
  ViridianCity_PokemonCenter_1F_EventScript_Nurse: [
    'Welcome to our POKEMON CENTER.',
    "We'll restore your POKEMON to full health.",
    'Thank you for waiting!',
    'Your POKEMON are fully healed.',
  ],
  ViridianCity_PokemonCenter_1F_EventScript_Gentleman: [
    'A trainer should always heal up before the next route.',
  ],
  ViridianCity_PokemonCenter_1F_EventScript_Boy: [
    'My POKEMON were down to 1 HP.',
    'The nurse saved us.',
  ],
  ViridianCity_PokemonCenter_1F_EventScript_Youngster: [
    "I'm headed to VIRIDIAN FOREST next.",
    'Lots of bugs in there!',
  ],

  // ── Pallet PlayersHouse 2F (MVP PR 6 / D5) ────────────────
  // The bedroom: NES, PC, the wall sign. Cartridge canon: NES boots
  // SNES-tier childhood nostalgia; PC is RED's home computer (mostly
  // unused for v0.1; future flag-gated item storage); the wall sign is
  // a hand-drawn map of Kanto from RED's adventure-prep phase.
  PalletTown_PlayersHouse_2F_EventScript_NES: [
    "An old game console.",
    "RED's saved games are in there.",
  ],
  PalletTown_PlayersHouse_2F_EventScript_PC: [
    "RED's home computer.",
    "(Nothing in storage yet.)",
  ],
  PalletTown_PlayersHouse_2F_EventScript_Sign: [
    "A hand-drawn map of KANTO.",
    'PALLET → VIRIDIAN → PEWTER, then north and east to the rest.',
  ],

  // ── Pallet RivalsHouse (MVP PR 7 / D6) ────────────────────
  // DAISY (BLUE's sister) wanders the room — cartridge canon dispenses
  // a haircut + advice; v0.1 leaves her dialogue-only. TownMap is
  // flag-hidden (FLAG_HIDE_TOWN_MAP — Daisy hands it over after the
  // first lab visit in cartridge). Bookshelf + picture are background.
  PalletTown_RivalsHouse_EventScript_Daisy: [
    "DAISY: Hi, RED!",
    'BLUE always pretends he\'s not excited.',
    "Don't let him fool you.",
  ],
  // Unreachable in practice — the map-object is permanently hidden;
  // DAISY grants the item (G9). Entry kept for lint coverage.
  PalletTown_RivalsHouse_EventScript_TownMap: ['A TOWN MAP of KANTO.'],
  PalletTown_RivalsHouse_EventScript_Bookshelf: [
    'Rows of POKEMON guidebooks.',
    'BLUE has read them all twice.',
  ],
  PalletTown_RivalsHouse_EventScript_Picture: [
    'A framed picture of BLUE and DAISY.',
    'They\'re holding fishing rods.',
  ],

  // ── Route 2 House (D9 — Mr Psychic's place; cartridge trade NPC) ──
  // In cartridge canon the GBA Kid here is the Mr. Mime trade partner.
  // v0.1 has no trades; both characters are dialogue-only stand-ins.
  Route2_House_EventScript_Scientist: [
    "POKEMON TRADES are powerful.",
    'A traded POKEMON grows faster!',
    '(Trading available in v0.2.)',
  ],
  Route2_House_EventScript_Reyley: [
    "REYLEY: I'd trade you my MR. MIME...",
    "...if I had any way to send him.",
    '(v0.2.)',
  ],

  // ── Route 2 East Building (D10 — Diglett's Cave gate) ────────────
  // In cartridge this gates passage to Diglett's Cave from Route 2.
  // v0.1 has no Diglett's Cave content; building is flavor.
  Route2_EastBuilding_EventScript_Aide: [
    "Welcome to ROUTE 2 EAST.",
    'The path east leads to DIGLETT\'S CAVE.',
    '(Not in v0.1 scope.)',
  ],
  Route2_EastBuilding_EventScript_Rocker: [
    "I'm in a band, you know.",
    'We play at the rocker bar in CELADON.',
    "(v0.2 maybe.)",
  ],

  // ── Viridian Mart (D8) + Pewter Mart (D11) ────────────────────────
  // Bucket F (Marts UI + money) is deferred entirely per the locked
  // plan. The CLERK ships as dead-end dialogue ("out of stock today");
  // the building converts so the friend group can walk in + look
  // around, but no purchase flow exists. Re-architect when post-
  // playtest signal makes Marts worth their own brainstorm.
  ViridianCity_Mart_EventScript_Clerk: [
    "Welcome to the POKEMON MART!",
    "I'm sorry — we're sold out right now.",
    "(Mart inventory lands in v0.2.)",
  ],
  ViridianCity_Mart_EventScript_Youngster: [
    "I'm saving up for SUPER REPELS.",
    "Wild POKEMON won't leave me alone in the forest.",
  ],
  ViridianCity_Mart_EventScript_Woman: [
    "POTIONS are essential.",
    "Always carry a few.",
  ],
  PewterCity_Mart_EventScript_Clerk: [
    "Welcome to the POKEMON MART!",
    "I'm sorry — we're sold out right now.",
    "(Mart inventory lands in v0.2.)",
  ],
  PewterCity_Mart_EventScript_Youngster: [
    "BROCK is tough but fair.",
    "Bring grass or water types.",
  ],
  PewterCity_Mart_EventScript_Boy: [
    'My dad works in the MUSEUM.',
    "It's full of OLD AMBER and FOSSILS!",
  ],
};

export function getDialogue(scriptRef: string): string[] {
  return DIALOGUE[scriptRef] ?? ['...'];
}
